import { AppRole, GroupRole, Permission, Player, Tournament, Group, GlobalConfig } from '../types';

// ID du propriétaire de l'application (non exposé publiquement)
export const APP_OWNER_ID = 'DLCrlUE1PfccpzLfVYvtXy3HdUz1';

// Matrice des permissions par défaut par rôle
export const DEFAULT_ROLE_PERMISSIONS: Record<string, Permission[]> = {
    // Propriétaire: Toutes les permissions
    owner: [
        'view_all_groups',
        'manage_groups',
        'manage_group_members',
        'manage_tournament_participants',
        'manage_app_users',
        'create_tournament',
        'manage_tournament',
        'assign_roles',
        'validate_scores',
        'view_audit_logs',
        'edit_any_profile',
        'manage_group_settings',
        'delete_group',
        'delete_tournament'
    ],
    // Admin application: Gérer les groupes et utilisateurs app
    app_admin: [
        'view_all_groups',
        'manage_groups',
        'manage_app_users',
        'assign_roles',
        'view_audit_logs',
        'delete_tournament'
    ],
    // Admin de groupe: Gérer son groupe  
    admin: [
        'manage_group_members',
        'create_tournament',
        'manage_tournament',
        'delete_tournament',
        'assign_roles',
        'manage_group_settings',
        'delete_group'
    ],
    // Administrateur Général: Presque tout sauf bannir de l'app
    co_admin: [
        'manage_group_members',
        'manage_tournament_participants',
        'create_tournament',
        'manage_tournament',
        'assign_roles',
        'validate_scores'
    ],
    // Organisateur: Gérer et créer des tournois
    organizer: [
        'create_tournament',
        'manage_tournament',
        'manage_tournament_participants',
        'validate_scores',
        'assign_roles', // Peut assigner arbitres
        'delete_tournament'
    ],
    // Arbitre: Valider scores
    referee: [
        'validate_scores'
    ],
    // Joueur: Permissions de base
    player: [],
    user: []
};

/**
 * Priorité des rôles (plus le nombre est élevé, plus le rôle est important)
 * Utilisé pour déterminer quel badge afficher quand un utilisateur a plusieurs rôles
 */
export const ROLE_PRIORITY: Record<string, number> = {
    owner: 100,
    app_admin: 90,
    co_admin: 80,
    admin: 70,
    organizer: 50,
    referee: 40,
    player: 10,
    user: 0
};

/**
 * Contexte d'affichage des badges
 */
export type BadgeDisplayContext = 'profile' | 'group_ranking' | 'tournament';

/**
 * Règles d'affichage des badges selon le rôle et le contexte
 */
export const BADGE_DISPLAY_RULES: Record<string, BadgeDisplayContext[]> = {
    owner: ['profile', 'group_ranking', 'tournament'],
    app_admin: ['profile', 'group_ranking', 'tournament'],
    admin: ['profile', 'group_ranking', 'tournament'],
    co_admin: ['profile', 'group_ranking', 'tournament'],
    organizer: ['tournament', 'profile', 'group_ranking'],
    referee: ['tournament', 'profile', 'group_ranking'],
    player: [],
    user: []
};

/**
 * Détermine le rôle de plus haute priorité d'un utilisateur dans un contexte donné
 */
export function getHighestRole(
    user: any,
    group?: Group | null,
    tournament?: Tournament | null
): GroupRole | AppRole {
    if (!user) return 'player';
    const userId = user.id || user.uid;

    let highestRole: GroupRole | AppRole = 'player';
    let highestPriority = ROLE_PRIORITY.player;

    // 1. Check global app role - Check by userId
    if (user.appRole === 'owner' || userId === APP_OWNER_ID) {
        return 'owner'; // Always highest
    }

    if (user.appRole === 'app_admin' && ROLE_PRIORITY.app_admin > highestPriority) {
        highestRole = 'app_admin';
        highestPriority = ROLE_PRIORITY.app_admin;
    }

    // 2. Check group role - FIRST check directly on user object
    if (user.groupRole && ROLE_PRIORITY[user.groupRole]) {
        const directGroupRolePriority = ROLE_PRIORITY[user.groupRole] || 0;
        if (directGroupRolePriority > highestPriority) {
            highestRole = user.groupRole;
            highestPriority = directGroupRolePriority;
        }
    }

    // 2b. Also check in group.players array as fallback
    if (group) {
        const player = group.players?.find(p => p.id === userId);
        if (player?.groupRole) {
            const groupRolePriority = ROLE_PRIORITY[player.groupRole] || 0;
            if (groupRolePriority > highestPriority) {
                highestRole = player.groupRole;
                highestPriority = groupRolePriority;
            }
        }
        // Legacy: Creator is admin
        // @ts-ignore
        if (group.creatorId === userId && ROLE_PRIORITY.admin > highestPriority) {
            highestRole = 'admin';
            highestPriority = ROLE_PRIORITY.admin;
        }
    }

    // 3. Check tournament role (only if lower priority than current)
    if (tournament) {
        if ((tournament.admins?.includes(userId) || tournament.organizers?.includes(userId))
            && ROLE_PRIORITY.organizer > highestPriority) {
            highestRole = 'organizer';
            highestPriority = ROLE_PRIORITY.organizer;
        }
        if (tournament.referees?.includes(userId) && ROLE_PRIORITY.referee > highestPriority) {
            highestRole = 'referee';
            highestPriority = ROLE_PRIORITY.referee;
        }
    }

    return highestRole;
}

/**
 * Détermine le rôle à afficher pour un joueur dans un contexte spécifique
 * Retourne null si aucun badge ne doit être affiché
 */
export function getDisplayRole(
    player: Player | any,
    context: BadgeDisplayContext,
    group?: Group | null,
    tournament?: Tournament | null
): GroupRole | AppRole | null {
    if (!player) return null;

    const userId = player.id || player.uid;

    // Récupérer le rôle le plus élevé
    const role = getHighestRole(player, group, tournament);

    // Vérifier si ce rôle doit être affiché dans ce contexte
    const displayContexts = BADGE_DISPLAY_RULES[role] || [];
    if (displayContexts.includes(context)) {
        return role;
    }

    // Si le rôle principal ne s'affiche pas dans ce contexte, 
    // chercher un rôle secondaire qui s'affiche

    // Pour le contexte tournoi, vérifier les rôles de tournoi
    if (context === 'tournament' && tournament) {
        if (tournament.admins?.includes(userId) || tournament.organizers?.includes(userId)) {
            return 'organizer';
        }
        if (tournament.referees?.includes(userId)) {
            return 'referee';
        }
    }

    return null;
}

/**
 * Détermine les rôles d'un utilisateur basé sur le contexte
 */
/**
 * Détermine les rôles d'un utilisateur basé sur le contexte
 */
export function getUserRole(
    user: any,
    group?: Group | null,
    tournament?: Tournament | null
): GroupRole | AppRole {
    if (!user) return 'player';
    const userId = user.id || user.uid;

    // 1. Vérifier si propriétaire (toujours prioritaire)
    if (user.appRole === 'owner' || userId === APP_OWNER_ID) {
        return 'owner';
    }

    // 2. Vérifier rôle global (App Admin)
    if (user.appRole === 'app_admin') {
        return 'app_admin';
    }

    // 3. Check Group Admin (Priority over Tournament roles)
    if (group) {
        const player = group.players?.find(p => p.id === userId);
        // Explicit check for 'admin' role in group
        if (player?.groupRole === 'admin') return 'admin';

        // Explicit check for 'co_admin' role in group (Priority over Organizer)
        if (player?.groupRole === 'co_admin') return 'co_admin';

        // Legacy check: Creator is admin
        // @ts-ignore
        if (group.creatorId === userId) return 'admin';
    }

    // 4. Check Tournament Roles (Organizer > Referee)
    if (tournament) {
        if (tournament.admins?.includes(userId) || tournament.organizers?.includes(userId)) return 'organizer';
        if (tournament.referees?.includes(userId)) return 'referee';
    }

    // 5. Check other group roles (Co-admin, etc.) if not already returned
    if (group) {
        const player = group.players?.find(p => p.id === userId);
        if (player?.groupRole) return player.groupRole;
    }

    // 6. Default
    return 'player';
}

/**
 * Vérifie si un utilisateur a une permission spécifique dans un contexte
 */
export function hasPermission(
    user: any,
    permission: Permission,
    context?: {
        groupId?: string;
        tournamentId?: string;
        group?: Group | null;
        tournament?: Tournament | null;
        globalConfig?: GlobalConfig | null;
    }
): boolean {
    if (!user) return false;

    // Le propriétaire a toutes les permissions
    // On utilise getUserRole pour bénéficier de la logique unifiée (DB + email)
    const role = getUserRole(user, context?.group, context?.tournament);
    if (role === 'owner') return true;

    // Vérifier si le rôle a la permission
    let rolePermissions = DEFAULT_ROLE_PERMISSIONS[role] || [];

    // 1. Priorité: Permissions du groupe spécifique
    if (context?.group?.rolePermissions && context.group.rolePermissions[role]) {
        // @ts-ignore
        rolePermissions = context.group.rolePermissions[role];
    }
    // 2. Fallback: Permissions globales configurées
    else if (context?.globalConfig?.rolePermissions && context.globalConfig.rolePermissions[role]) {
        // @ts-ignore
        rolePermissions = context.globalConfig.rolePermissions[role];
    }

    // 3. Marque Blanche overrides
    const wl = context?.globalConfig?.whiteLabel;
    if (wl?.enabled) {
        if (permission === 'create_tournament' && wl.permissions?.allowMemberTournamentCreation) {
            // Allow members to create tournaments
            return true;
        }
        if (permission === 'create_tournament' && wl.permissions?.allowMemberTournamentCreation === false) {
            // If explicitly false, ensure they aren't getting it just because they are simple members.
            // (They wouldn't anyway since DEFAULT doesn't have it, but just in case)
        }
    }

    return rolePermissions.includes(permission);
}

/**
 * Vérifie si un utilisateur peut éditer un joueur
 */
/**
 * Vérifie si un utilisateur peut éditer un joueur
 */
export function canEditPlayer(
    user: any,
    playerId: string,
    context?: {
        group?: Group | null;
        globalConfig?: GlobalConfig | null;
    }
): boolean {
    if (!user) return false;
    const userId = user.id || user.uid;

    // RULE 1: Cannot edit App Owner (by others)
    if (playerId === APP_OWNER_ID && userId !== playerId) return false;

    // Look up target player in context
    let targetIsOwner = false;
    // Check group context
    if (context?.group?.players) {
        const target = context.group.players.find(p => p.id === playerId);
        if (target && target.appRole === 'owner') {
            targetIsOwner = true;
        }
    }

    // RULE 2: Cannot edit any Owner (App or Group) if not self
    if (targetIsOwner && userId !== playerId) return false;

    // Propriétaire: toujours (unless target is protected above - wait, Owner should be able to edit almost everyone, EXCEPT other Owners possibly?)
    // User said: "Personne ne peut modif son profil". Implications: NOBODY else.
    // So if target is Owner, return FALSE for everyone else. (Handled above).

    // Propriétaire Actor
    if (getUserRole(user) === 'owner') return true;

    // Peut toujours éditer son propre profil  
    if (userId === playerId) return true;

    // Admin App peut tout éditer (EXCEPT owners, handled above)
    if (hasPermission(user, 'manage_app_users', context)) return true;

    // Admin du groupe peut éditer les membres de son groupe (si implémenté ainsi)
    // Pour l'instant on garde manage_app_users pour l'édition complète de profil

    return false;
}

/**
 * Vérifie si un utilisateur peut gérer un tournoi
 */
export function canManageTournament(
    user: any,
    tournament: Tournament | null,
    group?: Group | null
): boolean {
    if (!user || !tournament) return false;
    const userId = user.id || user.uid;

    // Propriétaire: toujours
    if (getUserRole(user) === 'owner') return true;

    // Admin du groupe
    if (group) {
        if (hasPermission(user, 'manage_tournament', { group })) return true;
    }

    // Organisateur du tournoi
    if (tournament.admins?.includes(userId)) return true;

    return false;
}

/**
 * Vérifie si un utilisateur peut assigner des rôles dans un groupe
 */
export function canAssignRoles(
    user: any,
    group?: Group | null
): boolean {
    if (!user) return false;

    // Propriétaire: toujours
    if (getUserRole(user) === 'owner') return true;

    // Admin du groupe
    if (group) {
        return hasPermission(user, 'assign_roles', { group });
    }

    return false;
}

/**
 * Vérifie si un utilisateur peut supprimer un joueur d'un groupe
 */
export function canDeletePlayer(
    user: any,
    playerId: string,
    context?: {
        group?: Group | null;
        globalConfig?: GlobalConfig | null;
    }
): boolean {
    if (!user) return false;
    const userId = user.id || user.uid;

    // RULE 1: Cannot delete App Owner (Hardcoded ID)
    if (playerId === APP_OWNER_ID) return false;

    // Propriétaire: toujours (unless target is also owner)
    // NOTE: If current user is Owner, they can delete anyone EXCEPT other Owners (handled below by check)
    // But wait, "Personne ne peut...".

    // Look up target player in group context to check their roles
    let targetIsOwner = false;
    if (context?.group?.players) {
        const target = context.group.players.find(p => p.id === playerId);
        if (target && target.appRole === 'owner') {
            targetIsOwner = true;
        }
    }

    // RULE 2: Cannot delete any Owner (App or Group)
    if (targetIsOwner) return false;

    // Propriétaire (Actor) Logic
    if (getUserRole(user) === 'owner') return true;

    // Ne peut pas se supprimer soi-même
    if (userId === playerId) return false;

    // Admin App peut tout supprimer
    if (hasPermission(user, 'manage_app_users', context)) return true;

    // Admin du groupe peut supprimer du groupe
    if (context?.group) {
        return hasPermission(user, 'manage_group_members', context);
    }

    return false;
}

/**
 * Retourne le label d'un rôle (localisé si t est fourni)
 */
export function getRoleLabel(role: GroupRole | AppRole, t?: any): string {
    if (t) {
        return t(`roles.${role}`, role);
    }
    const labels: Record<string, string> = {
        owner: 'Propriétaire',
        app_admin: 'Administrateur Global',
        admin: 'Administrateur de groupe',
        co_admin: 'Co-Administrateur',
        organizer: 'Organisateur de tournoi',
        referee: 'Arbitre',
        player: 'Joueur',
        user: 'Utilisateur'
    };
    return labels[role] || role;
}

/**
 * Retourne l'icône Material d'un rôle
 */
export function getRoleIcon(role: GroupRole | AppRole): string {
    const icons: Record<string, string> = {
        owner: 'workspace_premium',
        app_admin: 'admin_panel_settings',
        admin: 'shield',
        co_admin: 'security',
        organizer: 'event',
        referee: 'sports',
        player: 'person',
        user: 'person'
    };
    return icons[role] || 'person';
}

/**
 * Retourne la couleur d'un rôle
 */
export function getRoleColor(role: GroupRole | AppRole): string {
    const colors: Record<string, string> = {
        owner: 'text-yellow-500',
        app_admin: 'text-purple-500',
        admin: 'text-blue-500',
        co_admin: 'text-indigo-500',
        organizer: 'text-green-500',
        referee: 'text-orange-500',
        player: 'text-gray-400',
        user: 'text-gray-400'
    };
    return colors[role] || 'text-gray-400';
}

/**
 * Retourne les rôles qu'un utilisateur peut assigner selon son propre rôle
 */
export function getAssignableRoles(userRole: GroupRole | AppRole): GroupRole[] {
    const assignableRoles: Record<string, GroupRole[]> = {
        owner: ['admin', 'co_admin', 'organizer', 'referee', 'player'],
        app_admin: ['admin', 'co_admin', 'organizer', 'referee', 'player'],
        admin: ['co_admin', 'organizer', 'referee', 'player'],
        co_admin: ['organizer', 'referee', 'player'],
        organizer: ['referee', 'player'],
        referee: [],
        player: [],
        user: []
    };
    return assignableRoles[userRole] || [];
}

/**
 * Vérifie si un utilisateur peut assigner un rôle spécifique à quelqu'un d'autre
 */
export function canAssignRole(
    user: any,
    targetRole: GroupRole,
    group?: Group | null
): boolean {
    if (!user) return false;

    // Propriétaire: peut tout assigner (sauf modifier un autre owner ?)
    if (getUserRole(user) === 'owner') return true;

    // CANNOT assign/change role of an Owner if you are not an owner (Wait, logic above handles owner actor).
    // If I am Admin, can I change Owner's role? NO.
    // But `canAssignRole` checks if I can assign TARGET ROLE. It does not check TARGET USER's CURRENT ROLE.
    // The UI should check this separately or this function should take targetUser.
    // This function signature is `(user, targetRole, group)`. It doesn't know WHO gets the role.
    // So I cannot block demoting an owner HERE. I must block it in `RoleAssignmentButton` or similar.

    // However, I can ensure nobody can assign 'owner' role except existing owners (via assignable roles).

    // Obtenir le rôle de l'utilisateur
    const userRole = getUserRole(user, group);

    // Vérifier si le rôle cible est dans la liste des rôles assignables
    const assignableRoles = getAssignableRoles(userRole);
    return assignableRoles.includes(targetRole);
}
